// Radar live (Node Control): the radar stream as the backend stores it
// (devices/<mac>/radar/stream/) and the waterfall it serves
// (devices/<mac>/radar/waterfall/). Pure helpers: no React, network or storage.
import { finite, timeOf } from "utils/radarDiagnostics";
import {
  NEAR_FIELD_BINS,
  analyzeSpectrum,
  pickDistance,
  spectrumReachMm,
} from "utils/radarSignal";

export const STREAM_SPECTRUM = 1;
export const STREAM_ADC = 2;
export const STREAM_ECHOES = 4;
export const STREAM_DUMP = 8;
// Every 5 s needs the radar link at 38,400 baud or more (streamFloor()).
export const STREAM_CADENCES = [5, 10, 20, 30, 60];

// ---- The radar link (radar_baud, firmware .60) ----
// The radar's serial speed. The node reads back the rate the radar answers
// at; 9,600 is the radar's factory rate and the way back. Faster sends a
// capture's packets sooner, so captures and the time awake are shorter. The
// switch writes the radar's flash, and the node makes it only on a confirmed
// firmware and above the battery floor.
export const RADAR_BAUDS = [9600, 19200, 38400, 115200];
export const BAUD_FACTORY = 9600;
// The stream every 5 s needs 38,400 baud or more; slower links, 10 s.
export const FAST_STREAM_BAUD = 38400;
// A rate as the node reports it (a whole number of baud), else null.
export const readBaud = (value) => {
  const n = finite(value);
  return n !== null && Number.isInteger(n) && n > 0 ? n : null;
};
// "38,400 baud"; "9,600 baud (the radar's factory rate)".
export const baudText = (baud) =>
  `${baud.toLocaleString()} baud${baud === BAUD_FACTORY ? " (the radar's factory rate)" : ""}`;
// The stream's shortest cadence (s) at a rate, within the server's rule
// (radar_stream_s, [5, 600] from firmware .60): 5 s at 38,400 baud or more,
// else 10 s; an unknown rate counts as the factory 9,600.
export function streamFloor(baud, rule = [5, 600]) {
  const rate = readBaud(baud) ?? BAUD_FACTORY;
  return Math.max(finite(rule?.[0]) ?? 5, rate >= FAST_STREAM_BAUD ? 5 : 10);
}
// One of the radar's packets (a spectrum or a beat signal, 1,036-1,042 bytes)
// on the wire at a rate, 10 bits a byte: about 1.1 s at 9,600 baud.
export const PACKET_BYTES = 1040;
export const packetSeconds = (baud) => (PACKET_BYTES * 10) / baud;
// A switch's outcome, which rides once in the next reading (jjy_baud_set
// {req, from, to, ok, why, ms}): the rate asked for; the rate before (null: a
// rescue, the radar answered at no rate) and after (null: silent at every
// rate); whether it took; the node's word for how - ok, already or rescued
// with ok true; refused, reboot, kept, found, reverted or silent with ok false
// - and how long it took. Null without one.
export function baudSwitch(value) {
  if (!value || typeof value !== "object") return null;
  const why = typeof value.why === "string" ? value.why.trim() : "";
  return {
    req: readBaud(value.req),
    from: readBaud(value.from),
    to: readBaud(value.to),
    ok: value.ok === true || value.ok === 1,
    why: why || null,
    ms: finite(value.ms),
  };
}
// A switch that did not take, in words (else the node's own word).
const BAUD_WHY = {
  refused: "refused",
  reboot: "stored by the radar for its next power-up",
  kept: "the radar kept its rate",
  found: "found at a third rate",
  reverted: "silent at the new rate, so sent back to the old one",
  silent: "silent at every rate since",
};
// "switched from 9,600 to 115,200 baud in 4.8 s"; "already at 115,200
// baud"; "rescued: the radar answered at no rate, now at 9,600 baud"; "not
// switched to 115,200 baud: silent at the new rate, so sent back to the old
// one (4.8 s); the radar answers at 9,600 baud".
export function baudSwitchText(s) {
  if (!s) return "";
  const rate = (v) => `${v.toLocaleString()} baud`;
  const took = s.ms !== null ? `${(s.ms / 1000).toFixed(1)} s` : null;
  if (s.ok) {
    const to = s.to ?? s.req;
    if (s.why === "already")
      return `already at ${to !== null ? rate(to) : "the rate asked for"}`;
    if (s.why === "rescued")
      return `rescued: the radar answered at no rate${to !== null ? `, now at ${rate(to)}` : ""}${took ? ` (${took})` : ""}`;
    return `switched${s.from !== null ? ` from ${s.from.toLocaleString()}` : ""}${to !== null ? ` to ${rate(to)}` : ""}${took ? ` in ${took}` : ""}`;
  }
  const words = s.why ? BAUD_WHY[s.why] || s.why : null;
  const now =
    s.to !== null
      ? `; the radar answers at ${rate(s.to)}`
      : s.why === "silent"
        ? ""
        : "; the radar answers at no rate";
  return `not switched${s.req !== null ? ` to ${rate(s.req)}` : ""}${words ? `: ${words}` : ""}${took ? ` (${took})` : ""}${now}`;
}
// The node's search for the radar's rate (jjy_baud {bd, exp, n, ms}), sent
// when the radar answered at another rate than expected, needed more than one
// poll or answered at none: the rate it answered at (null: none), the rate
// expected, the polls and how long they took. A bare number is the rate
// found. Null without one.
export function baudFound(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== "object") {
    const bd = readBaud(value);
    return bd === null ? null : { bd, exp: null, n: null, ms: null };
  }
  return {
    bd: readBaud(value.bd),
    exp: readBaud(value.exp),
    n: finite(value.n),
    ms: finite(value.ms),
  };
}
// "9,600 baud, not the 115,200 expected (3 polls, 1.2 s)"; "no rate answered
// (115,200 baud expected; 8 polls, 6.0 s)".
export function baudFoundText(f) {
  if (!f) return "";
  const polls = [
    f.n !== null ? `${f.n} poll${f.n === 1 ? "" : "s"}` : null,
    f.ms !== null ? `${(f.ms / 1000).toFixed(1)} s` : null,
  ]
    .filter(Boolean)
    .join(", ");
  if (f.bd === null)
    return `no rate answered${f.exp !== null || polls ? ` (${[f.exp !== null ? `${f.exp.toLocaleString()} baud expected` : null, polls || null].filter(Boolean).join("; ")})` : ""}`;
  return `${f.bd.toLocaleString()} baud${f.exp !== null && f.exp !== f.bd ? `, not the ${f.exp.toLocaleString()} expected` : ""}${polls ? ` (${polls})` : ""}`;
}
export const STREAM_WINDOWS = [
  [15, "15 min"],
  [60, "1 h"],
  [180, "3 h"],
  [720, "12 h"],
];
export const STREAM_LIMIT = 300; // the stream endpoint's page size
export const WATERFALL_COLUMNS = 720;
export const STREAM_POLL_MS = 5000;
export const RECENT_MS = 10 * 60 * 1000;
// What one capture message costs over the radio, as the node sends it: each
// radar packet base64-encoded (4/3 of the spectrum's 1,042 B, the threshold
// block's 1,028 B and the ADC packet's 1,036 B), the echo list as text (up to
// about 400 B) and about 360 B of message framing. A full capture with every
// part is about 4.9 kB, as the backend measures it.
export const MESSAGE_BYTES = {
  framing: 360,
  spectrum: 1389,
  threshold: 1371,
  adc: 1381,
  echoes: 400,
};

// radar_stream as read back: an integer 0-15, else null (not reported).
export function readMask(value) {
  const v = finite(value);
  return v !== null && Number.isInteger(v) && v >= 0 && v <= 15 ? v : null;
}

// radar_raw_mode (firmware 0.3.4-rtct.55 or later): the value the node sends
// in AT+OUTRAWDATA=<mode> to switch the radar's raw output on; earlier
// firmware always sends 1. As probed on node 1's radar (firmware 20260424,
// CPUTAG AT6010): mode 1 sends spectrum packets (1,042 B, about two in each
// 8 s window), mode 2 beat-signal (ADC) packets (1,036 B, two in each 8 s
// window), mode 4 the same spectrum as mode 1 (correlation 1.0) at about one
// in each 8 s window; modes 3 and 5 are accepted but send nothing, mode 6
// sends its samples as a stream of text the node does not store, and mode 7
// is refused ("ValueErr"). A mode that brings nothing is switched off by the
// node after three captures without a packet. The radar sends one kind of
// packet per mode: no capture holds both the spectrum and the beat signal;
// firmware .56 can alternate captures between modes 1 and 2
// (radar_raw_alt).
export const RAW_MODE_DEFAULT = 1;
export const RAW_MODES = [
  [1, "Spectrum (verified)"],
  [2, "Beat signal (verified)"],
  [3, "Sends nothing on this radar"],
  [4, "Spectrum, slower (verified)"],
  [5, "Sends nothing on this radar"],
  [6, "Text samples - not stored"],
  [7, "Refused by this radar"],
];
// The raw parts (radar_stream and radar_burst bits) each mode brings.
const RAW_MODE_SENDS = {
  1: STREAM_SPECTRUM,
  2: STREAM_ADC,
  3: 0,
  4: STREAM_SPECTRUM,
  5: 0,
  6: 0,
  7: 0,
};
export const rawModeSends = (mode) => RAW_MODE_SENDS[mode] ?? null;
const NOTHING = {
  3: "sends nothing on this radar",
  5: "sends nothing on this radar",
  6: "sends its samples as text, which the node does not store",
  7: 'is refused by this radar ("ValueErr")',
};

// radar_raw_alt (firmware .56) as read back: 1 when the node alternates
// captures between the spectrum (mode 1) and the beat signal (mode 2), 0
// when not, null when not reported.
export function readRawAlt(value) {
  const v = finite(value);
  return v === 0 || v === 1 ? v : null;
}

// The parts a capture will hold in a mode: of the raw parts ticked, the one
// the mode sends, plus the rest. While alternating (`alt`), captures take
// turns in modes 1 and 2; `alt` says which turn ("spectrum" or "beat").
export function partsSent(mask, mode, alt = null) {
  const m = readMask(mask) ?? 0;
  const raw = STREAM_SPECTRUM | STREAM_ADC;
  const sends =
    alt === "spectrum"
      ? STREAM_SPECTRUM
      : alt === "beat"
        ? STREAM_ADC
        : rawModeSends(mode);
  return sends === null ? m : (m & ~raw) | (m & sends);
}

// A hint for the parts ticked in a mode, or "": what will not arrive, and
// what sends it.
export function rawModeHint(
  mode,
  mask,
  { alt = false, altAllowed = false } = {},
) {
  const m = readMask(mask) ?? 0;
  if (alt)
    return m & STREAM_SPECTRUM && m & STREAM_ADC
      ? ""
      : "While alternating, every other capture is a beat signal (mode 2) and the rest are spectra (mode 1): tick both, or half the captures bring nothing.";
  const sends = rawModeSends(mode);
  const or = altAllowed ? ", or alternate the two" : "";
  if (sends === null) return "";
  if (sends === 0)
    return `Raw output mode ${mode} ${NOTHING[mode] || "sends nothing on this radar"}: the node switches it off after 3 captures without a packet. Mode 1 sends the spectrum, mode 2 the beat signal.`;
  if (m & STREAM_ADC && !(sends & STREAM_ADC))
    return `Raw output mode ${mode} sends only the spectrum, so no beat signal arrives; mode 2 sends the beat signal, without the spectrum${or}.`;
  if (m & STREAM_SPECTRUM && !(sends & STREAM_SPECTRUM))
    return `Raw output mode ${mode} sends only the beat signal, so no spectrum arrives; mode 1 sends the spectrum, without the beat signal${or}.`;
  return "";
}

// radar_raw_mode as read back: an integer 1-7, else null (not reported).
export function readRawMode(value) {
  const v = finite(value);
  return v !== null && Number.isInteger(v) && v >= 1 && v <= 7 ? v : null;
}
export const rawModeLabel = (mode) =>
  RAW_MODES.find(([m]) => m === mode)?.[1] || "";
// "3 · spectrum + beat signal?"
export const rawModeText = (mode) => `${mode} · ${rawModeLabel(mode)}`;

// The raw output modes to offer: those within the server's rule, then any
// other value that is shown or reported.
export function rawModeChoices(rule, ...extra) {
  const [lo, hi] = Array.isArray(rule) ? rule : [1, 7];
  const options = RAW_MODES.filter(([m]) => m >= lo && m <= hi);
  for (const m of new Set(extra))
    if (m !== null && m !== undefined && !options.some(([v]) => v === m))
      options.push([m, rawModeLabel(m)]);
  return options;
}

// A window of minutes in words: "15 min", "1 h", "24 h", "7 days".
export const windowText = (minutes) =>
  minutes >= 2880 && minutes % 1440 === 0
    ? `${minutes / 1440} days`
    : minutes >= 60
      ? `${minutes / 60} h`
      : `${minutes} min`;

// What one capture's raw-output window brought. Firmware .55 reports it with
// each capture (raw_mode, n_fft, n_adc, n_thr, end; null when absent);
// without the counts, what was stored stands in, one packet per part.
export function captureArrival(capture) {
  const count = (key, has) => {
    const n = finite(capture?.[key]);
    return n !== null && n >= 0 ? Math.round(n) : has ? 1 : 0;
  };
  const end = typeof capture?.end === "string" ? capture.end.trim() : "";
  return {
    mode: readRawMode(capture?.raw_mode),
    spectrum: count("n_fft", hasLevels(capture?.db)),
    adc: count("n_adc", !!capture?.has_adc),
    threshold: count("n_thr", !!capture?.has_thr),
    counted: ["n_fft", "n_adc", "n_thr"].some(
      (key) => finite(capture?.[key]) !== null,
    ),
    end: end || null,
  };
}

const arrivalCounts = (a) =>
  `spectrum ${a.spectrum.toLocaleString()}, beat signal ${a.adc.toLocaleString()}, threshold ${a.threshold.toLocaleString()}`;
const modeName = (mode) =>
  mode === null ? "mode not reported" : `mode ${mode}`;

// "mode 3 · spectrum 1, beat signal 0, threshold 0 · window ended: timeout"
export function arrivalText(capture) {
  const a = captureArrival(capture);
  return [
    modeName(a.mode),
    `${arrivalCounts(a)}${a.counted ? "" : " (as stored; this firmware does not count packets)"}`,
    a.end ? `window ended: ${a.end.replaceAll("_", " ")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

// Captures by raw output mode, the newest capture's mode first, with the
// packets that arrived summed: [{mode, captures, spectrum, adc, threshold}].
export function arrivalTally(captures) {
  const groups = new Map();
  for (const c of captures || []) {
    const a = captureArrival(c);
    const g = groups.get(a.mode) || {
      mode: a.mode,
      captures: 0,
      spectrum: 0,
      adc: 0,
      threshold: 0,
      last: -Infinity,
    };
    g.captures += 1;
    g.spectrum += a.spectrum;
    g.adc += a.adc;
    g.threshold += a.threshold;
    g.last = Math.max(g.last, timeOf(c?.t) ?? -Infinity);
    groups.set(a.mode, g);
  }
  return [...groups.values()]
    .sort((a, b) => b.last - a.last)
    .map(({ last, ...g }) => g);
}

// "mode 3: 12 captures, spectrum 12, beat signal 0, threshold 0"
export const tallyText = (g) =>
  `${modeName(g.mode)}: ${g.captures.toLocaleString()} capture${g.captures === 1 ? "" : "s"}, ${arrivalCounts(g)}`;

// How the radar answered the echo-list query (a capture's
// context.peaks_reply, {r, n, mi, text}): false when it does not provide an
// echo list (firmware .55 says "unsupported"; .54 reports "other" with the
// reply "Undefined"; "no_exist" is the radar's own "no such command"), true
// when it answered, null when that is not known.
export function echoListSupported(reply) {
  if (!reply || typeof reply !== "object") return null;
  const r = String(reply.r ?? "").toLowerCase();
  if (r === "unsupported" || r === "no_exist") return false;
  if (r === "other" && /\bundefined\b/i.test(String(reply.text ?? "")))
    return false;
  return r === "value" || r === "ok" ? true : null;
}

// The radar's own words in such a reply, without the echoed command:
// "AT+PEAKPREDICT\nUndefined\n" -> "Undefined".
export const echoReplyAnswer = (reply) =>
  String(reply?.text ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !/^AT\+PEAKPREDICT$/i.test(line))
    .join(" ");

// Approximate radio volume of a stream: bytes per capture message, per hour
// and per day. The threshold block comes with the spectrum when the radar
// sends one (counted unless `threshold` is false). The settings dump goes
// once, so it does not count per hour.
export function streamEstimate(mask, seconds, { threshold = true } = {}) {
  const m = readMask(mask);
  const s = finite(seconds);
  if (
    !m ||
    !(m & (STREAM_SPECTRUM | STREAM_ADC | STREAM_ECHOES)) ||
    !s ||
    s <= 0
  )
    return { capture: 0, hour: 0, day: 0 };
  let capture = MESSAGE_BYTES.framing;
  if (m & STREAM_SPECTRUM)
    capture +=
      MESSAGE_BYTES.spectrum + (threshold ? MESSAGE_BYTES.threshold : 0);
  if (m & STREAM_ADC) capture += MESSAGE_BYTES.adc;
  if (m & STREAM_ECHOES) capture += MESSAGE_BYTES.echoes;
  return { capture, hour: (capture * 3600) / s, day: (capture * 86400) / s };
}

export const formatSize = (bytes) =>
  bytes < 1e6
    ? `${Math.max(0.1, bytes / 1000).toLocaleString(undefined, { maximumFractionDigits: bytes < 1e4 ? 1 : 0 })} kB`
    : `${(bytes / 1e6).toLocaleString(undefined, { maximumFractionDigits: bytes < 1e7 ? 1 : 0 })} MB`;

export const formatRate = (estimate) =>
  estimate?.hour
    ? `${formatSize(estimate.hour)} per hour (${formatSize(estimate.day)} a day)`
    : "nothing per hour";

// Standard or URL-safe base64, whitespace and missing padding allowed.
export function decodeBase64(text) {
  if (typeof text !== "string" || !text) return new Uint8Array(0);
  let clean = text.replace(/\s+/g, "").replace(/-/g, "+").replace(/_/g, "/");
  while (clean.length % 4) clean += "=";
  const binary = atob(clean);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

// The waterfall endpoint's answer -> columns (captures) oldest first, each
// with its time, the radar's distance and one uint8 code per bin: dB = db_min
// + code × db_step, code 0 = at or below db_min, or no value (db_min is null
// when the window holds no values). A column of zeros holds no spectrum.
// Times without codes (a short payload) are left out and flagged.
export function decodeWaterfall(data) {
  const times = Array.isArray(data?.t) ? data.t : [];
  const bins = finite(data?.bins) || 256;
  const codes = decodeBase64(data?.codes);
  const n = Math.min(times.length, Math.floor(codes.length / bins));
  const air = Array.isArray(data?.air_mm) ? data.air_mm : [];
  const columns = [];
  for (let i = 0; i < n; i++) {
    const ms = timeOf(times[i]);
    if (ms === null) continue;
    const slice = codes.subarray(i * bins, (i + 1) * bins);
    columns.push({
      t: times[i],
      ms,
      air: finite(air[i]),
      codes: slice,
      empty: slice.every((c) => c === 0),
    });
  }
  columns.sort((a, b) => a.ms - b.ms);
  return {
    columns,
    bins,
    step: finite(data?.step_mm),
    cal: finite(data?.cal_mm) ?? 0,
    dbMin: finite(data?.db_min),
    dbStep: finite(data?.db_step) || 0.5,
    dbUnit: typeof data?.db_unit === "string" ? data.db_unit : null,
    decimated: !!data?.decimated,
    short: times.length > n,
  };
}

// A column's levels in dB; code 0 (at or below the scale, or no value) and
// a missing scale give NaN, which the analysis leaves out.
export function columnDb(column, wf) {
  const out = new Float32Array(column.codes.length);
  for (let i = 0; i < out.length; i++) {
    const code = column.codes[i];
    out[i] =
      code === 0 || wf.dbMin === null ? NaN : wf.dbMin + code * wf.dbStep;
  }
  return out;
}

export function quantizeDb(db, dbMin, dbStep, bins = db?.length || 0) {
  const out = new Uint8Array(bins);
  if (finite(dbMin) === null) return out;
  for (let i = 0; i < bins; i++) {
    const v = finite(db?.[i]);
    out[i] =
      v === null
        ? 0
        : Math.max(0, Math.min(255, Math.round((v - dbMin) / dbStep)));
  }
  return out;
}

function hasLevels(db) {
  return Array.isArray(db) && db.some((v) => finite(v) !== null);
}

// Captures that arrived after the waterfall was loaded become columns (on the
// waterfall's own dB codes; a window without values takes its scale from
// them); columns older than `fromMs` drop off. A capture without a spectrum
// (beat signal only, say) is a gap: an empty column, also between the
// waterfall's own columns, since the server leaves such captures out.
// Returns the same object when nothing changes.
export function appendCaptures(wf, captures, fromMs = -Infinity) {
  if (!wf) return wf;
  const lastMs = wf.columns.length
    ? wf.columns[wf.columns.length - 1].ms
    : -Infinity;
  const fresh = [...(captures || [])]
    .filter((c) => (timeOf(c?.t) ?? -Infinity) > lastMs)
    .sort((a, b) => timeOf(a.t) - timeOf(b.t));
  const taken = new Set(wf.columns.map((c) => c.ms));
  const gap = (c, ms) => ({
    t: c.t,
    ms,
    air: finite(c.air_mm),
    codes: new Uint8Array(wf.bins),
    empty: true,
  });
  const holes = [];
  for (const c of captures || []) {
    const ms = timeOf(c?.t);
    if (ms === null || ms > lastMs || taken.has(ms) || hasLevels(c.db))
      continue;
    holes.push(gap(c, ms));
    taken.add(ms);
  }
  let dbMin = wf.dbMin;
  if (dbMin === null) {
    let low = Infinity;
    for (const c of fresh)
      for (const v of Array.isArray(c.db) ? c.db : []) {
        const n = finite(v);
        if (n !== null && n < low) low = n;
      }
    if (Number.isFinite(low))
      dbMin = Math.floor(low / wf.dbStep) * wf.dbStep - wf.dbStep;
  }
  const added = [];
  let last = lastMs;
  for (const c of fresh) {
    const ms = timeOf(c.t);
    if (ms <= last) continue;
    if (!hasLevels(c.db)) added.push(gap(c, ms));
    else {
      const codes = quantizeDb(c.db, dbMin, wf.dbStep, wf.bins);
      added.push({
        t: c.t,
        ms,
        air: finite(c.air_mm),
        codes,
        empty: codes.every((x) => x === 0),
      });
    }
    last = ms;
  }
  const kept = wf.columns
    .concat(holes)
    .filter((c) => c.ms >= fromMs)
    .sort((a, b) => a.ms - b.ms);
  if (!added.length && !holes.length && kept.length === wf.columns.length)
    return wf;
  return { ...wf, dbMin, columns: kept.concat(added) };
}

// Colour limits in codes: the 20th percentile (about the noise) to the
// 99.8th, at least 24 codes (12 dB at 0.5 dB steps) apart. Code 0 (at or
// below the scale, or no value) and the near field (leakage, far stronger
// than any echo) do not count; the near field is drawn at the top colour.
export function colourRange(columns, { skip = NEAR_FIELD_BINS } = {}) {
  const hist = new Array(256).fill(0);
  let n = 0;
  for (const c of columns || []) {
    if (c.empty) continue;
    for (let i = skip; i < c.codes.length; i++) {
      const code = c.codes[i];
      if (!code) continue;
      hist[code] += 1;
      n += 1;
    }
  }
  if (!n) return null;
  const rank = (q) => {
    const target = q * (n - 1);
    let seen = 0;
    for (let i = 0; i < 256; i++) {
      seen += hist[i];
      if (seen > target) return i;
    }
    return 255;
  };
  const lo = rank(0.2);
  return { lo, hi: Math.min(255, Math.max(rank(0.998), lo + 24)) };
}

// A perceptual ramp (dark violet -> yellow, like plasma): equal steps look
// like equal changes, and it stays readable in greyscale.
export const RAMP = [
  [13, 8, 135],
  [84, 2, 163],
  [139, 10, 165],
  [185, 50, 137],
  [219, 92, 104],
  [244, 136, 73],
  [254, 188, 43],
  [240, 249, 33],
];
export function rampColor(t) {
  const x =
    Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0)) * (RAMP.length - 1);
  const i = Math.min(RAMP.length - 2, Math.floor(x));
  const f = x - i;
  return RAMP[i].map((v, k) => Math.round(v + (RAMP[i + 1][k] - v) * f));
}
export const rampGradient = () =>
  `linear-gradient(90deg, ${RAMP.map(
    (c, i) =>
      `rgb(${c.join(", ")}) ${Math.round((i / (RAMP.length - 1)) * 100)}%`,
  ).join(", ")})`;

// The time each column covers: until the next one, unless the gap is longer
// than three typical spacings (at least a minute), then one typical spacing.
export function columnSpans(columns) {
  const gaps = [];
  for (let i = 1; i < columns.length; i++) {
    const g = columns[i].ms - columns[i - 1].ms;
    if (g > 0) gaps.push(g);
  }
  gaps.sort((a, b) => a - b);
  const typical = gaps.length ? gaps[Math.floor(gaps.length / 2)] : 30000;
  const hold = Math.max(3 * typical, 60000);
  return columns.map((c, i) => {
    const next = columns[i + 1];
    return {
      start: c.ms,
      end: next && next.ms - c.ms <= hold ? next.ms : c.ms + typical,
      typical,
    };
  });
}

// The column covering a time, or null (a gap, or outside the columns).
export function columnAt(spans, ms) {
  let lo = 0;
  let hi = spans.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (spans[mid].start <= ms) {
      found = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return found >= 0 && ms < spans[found].end ? found : null;
}

// Stream captures kept in the browser: deduplicated (by id), oldest first,
// none older than `fromMs` except the newest one. Returns `old` unchanged
// when nothing was added or dropped.
export function mergeCaptures(old, incoming, fromMs = -Infinity) {
  const key = (c) => c.id ?? c.t;
  const byKey = new Map();
  for (const c of old || []) byKey.set(key(c), c);
  let added = 0;
  for (const c of incoming || [])
    if (c && timeOf(c.t) !== null && !byKey.has(key(c))) {
      byKey.set(key(c), c);
      added += 1;
    }
  const all = [...byKey.values()].sort((a, b) => timeOf(a.t) - timeOf(b.t));
  const kept = all.filter((c) => timeOf(c.t) >= fromMs);
  const next = kept.length ? kept : all.slice(-1);
  return !added && next.length === (old || []).length ? old || [] : next;
}

// The typical spacing of captures in ms, once there are at least `min` of
// them, else null: the mean of the middle half of the gaps (an interquartile
// mean), so a pause in the stream does not stretch it. A capture waits for
// the next live sample, so at radar_stream_s 20 they come 20 or 30 s apart;
// a plain median would flip between the two, the middle half gives ~25 s.
export function observedSpacing(captures, min = 5) {
  const times = (captures || [])
    .map((c) => timeOf(c?.t))
    .filter((t) => t !== null)
    .sort((a, b) => a - b);
  if (times.length < min) return null;
  const gaps = [];
  for (let i = 1; i < times.length; i++)
    if (times[i] > times[i - 1]) gaps.push(times[i] - times[i - 1]);
  return middleHalfMean(gaps);
}

// The mean of the middle half of some values (an interquartile mean), or
// null without any.
export function middleHalfMean(values) {
  const sorted = (values || [])
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);
  if (!sorted.length) return null;
  const cut = Math.floor(sorted.length / 4);
  const middle = sorted.slice(cut, sorted.length - cut);
  return middle.reduce((sum, v) => sum + v, 0) / middle.length;
}

// Missing sequence numbers between consecutive captures, or null when the
// captures carry none (a jump of 1,000 or more is taken as a restart).
export function seqGaps(captures) {
  let gaps = 0;
  let pairs = 0;
  for (let i = 1; i < (captures || []).length; i++) {
    const a = finite(captures[i - 1].seq);
    const b = finite(captures[i].seq);
    if (!Number.isInteger(a) || !Number.isInteger(b)) continue;
    pairs += 1;
    if (b - a > 1 && b - a < 1000) gaps += b - a - 1;
  }
  return pairs ? gaps : null;
}

// A waterfall column standing in for a capture that is no longer stored: its
// spectrum on 0.5 dB steps and the radar's distance, nothing else.
export function columnCapture(column, wf) {
  return {
    id: null,
    t: column.t,
    air_mm: column.air,
    peak_bin: null,
    step_mm: wf.step,
    cal_mm: wf.cal,
    mode: "waterfall",
    seq: null,
    has_adc: false,
    has_thr: false,
    db: Array.from(columnDb(column, wf), (v) =>
      Number.isFinite(v) ? v : null,
    ),
    thr_db: null,
    peaks: null,
    fromWaterfall: true,
  };
}

// One trend point per waterfall column: the SNR of the echo the radar chose,
// near-field energy over the floor, echoes over our threshold, the radar's
// distance (none when it is not a valid pick: 0, or beyond the spectrum).
// The waterfall carries no threshold curve or echo list.
export function columnTrend(column, wf) {
  const pick = pickDistance(
    column.air,
    spectrumReachMm(wf.step, wf.cal, wf.bins),
  );
  if (column.empty)
    return { ms: column.ms, snr: null, near: null, echoes: null, pick };
  const a = analyzeSpectrum({
    db: columnDb(column, wf),
    air_mm: column.air,
    step_mm: wf.step,
    cal_mm: wf.cal,
  });
  return {
    ms: column.ms,
    snr: a?.chosen?.snr ?? null,
    near: a?.nearField?.overFloor ?? null,
    echoes: a ? a.echoes.count : null,
    pick,
  };
}

// Whether a capture holds a spectrum: `db` always has 256 entries, null
// where the radar sent nothing positive.
export const hasSpectrum = (capture) => hasLevels(capture?.db);

// Chart rows for a capture's spectrum between two distances (mm): the
// reported distance of each bin (bin × step - cal), its level and the
// radar's threshold there.
export function spectrumRows(capture, from = -Infinity, to = Infinity) {
  const db = Array.isArray(capture?.db) ? capture.db : [];
  const thr = Array.isArray(capture?.thr_db) ? capture.thr_db : null;
  const step = finite(capture?.step_mm) || 30;
  const cal = finite(capture?.cal_mm) ?? 0;
  const rows = [];
  for (let i = 0; i < db.length; i++) {
    const d = i * step - cal;
    if (d < from || d > to) continue;
    rows.push({ d, db: finite(db[i]), thr: thr ? finite(thr[i]) : null });
  }
  return rows;
}

// The radar's settings dump for a capture: the reply to its settings query as
// the backend keeps it (context.getconfig), else a plain `dump` text.
export function settingsDumpOf(detail) {
  const gc = detail?.context?.getconfig;
  if (gc && typeof gc.text === "string" && gc.text.trim())
    return { text: gc.text, answer: gc.r ?? null };
  const text = [detail?.dump, detail?.context?.dump].find(
    (v) => typeof v === "string" && v.trim(),
  );
  return text ? { text, answer: null } : null;
}

// The settings dump as rows: "[SYSTEMCONFIG]", then AT+KEY=VALUE lines, the
// echoed AT+GETCONFIG and OK. KEY=VALUE without AT+ is read too; lines of any
// other shape are kept as they are (`other`).
export function parseSettingsDump(text) {
  const rows = [];
  const other = [];
  let section = null;
  let ok = false;
  for (const raw of String(text ?? "").split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const head = /^\[([^\]]+)\]$/.exec(line);
    const pair = /^(?:AT\+)?([A-Z][A-Z0-9_]*)=(.*)$/i.exec(line);
    if (head && section === null) section = head[1];
    else if (/^OK$/i.test(line)) ok = true;
    else if (/^(?:AT\+)?GETCONFIG$/i.test(line)) continue;
    else if (pair)
      rows.push({ key: pair[1].toUpperCase(), value: pair[2].trim() });
    else other.push(line);
  }
  return { section, rows, ok, other };
}

// The radar's own identifier: shown only on request.
export const PRIVATE_SETTINGS = ["SID"];
export const HIDDEN_VALUE = "••••••";
export const maskSettingsText = (text) =>
  String(text ?? "").replace(
    /^(\s*(?:AT\+)?SID=)(.+)$/gim,
    (_, head) => `${head}${HIDDEN_VALUE}`,
  );

const number = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};
const metres = (value) => {
  const m = number(value);
  return m === null ? null : formatMmText(m * 1000);
};
const formatMmText = (mm) =>
  `${mm.toLocaleString(undefined, { maximumFractionDigits: 1 })} mm`;
const MONTHS = "Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec".split(" ");
const SETTING_MEANINGS = {
  VERSION: (v) => {
    const m = /^(\d{4})(\d{2})(\d{2})$/.exec(v);
    return `The radar's firmware version${m && MONTHS[m[2] - 1] ? ` (it reads as a date: ${Number(m[3])} ${MONTHS[m[2] - 1]} ${m[1]})` : ""}.`;
  },
  SID: () => "The radar's own identifier (serial number).",
  DEVTYPE: () => "Device type: the vendor's model code.",
  CPUTAG: () => "The chip the radar runs on (the vendor's tag).",
  BAUDRATE: (v) => {
    const n = number(v);
    return `Serial speed between the radar and the node, bits per second${n ? ` (about ${Math.round(n / 10).toLocaleString()} bytes a second)` : ""}.`;
  },
  COLLECTFREQ: (v) => {
    const n = number(v);
    return `Measurement period in ms${n > 0 ? `: a measurement every ${n.toLocaleString()} ms, about ${Math.round(1000 / n).toLocaleString()} a second` : ""}.`;
  },
  RANGE: (v) => `Maximum range in metres${metres(v) ? ` (${metres(v)})` : ""}.`,
  BLINDZONE: (v) =>
    `Blind zone in metres${metres(v) ? `: echoes nearer than ${metres(v)} are ignored` : ""}.`,
  CALIBRATION: (v) =>
    `Calibration offset in mm${number(v) !== null ? `: each distance is the radar's range minus ${formatMmText(number(v))} (cal_mm)` : ""}.`,
  INSTALLHEIGHT: (v) =>
    number(v)
      ? `Install height in metres (${metres(v)}), for the water-depth output.`
      : "Install height in metres, for the water-depth output; 0 = not set.",
  LIMITFILTEROFXCM: (v, all) => {
    const m = /^\s*(\d+(?:\.\d+)?)\s*cm\s*,\s*(\d+)\s*$/i.exec(v);
    if (!m)
      return "The radar's own statistical filter (a jump size and a count).";
    const period = number(all.COLLECTFREQ);
    const seconds = period > 0 ? (Number(m[2]) * period) / 1000 : null;
    return `The radar's own statistical filter: a jump over ${m[1]} cm must persist for ${m[2]} measurements${seconds !== null ? ` (about ${seconds.toLocaleString(undefined, { maximumFractionDigits: 1 })} s at ${period} ms)` : ""}.`;
  },
  RANGERES: (v) =>
    `Metres per spectrum cell${metres(v) ? `: one bin is ${metres(v)}` : ""}.`,
  AUTOOUTDATACYCLE: (v) =>
    number(v) === 0
      ? "Automatic output period; 0 = off: the radar answers only when asked."
      : "Automatic output period (push output).",
  PROTOCOLTYPE: () => "Protocol on the serial line (the vendor's numbering).",
  MBDEVADDR: () => "Modbus device address.",
  MCUCFG: () => "Processor configuration (the vendor's).",
};

// A plain-English meaning for the radar's known settings, else "". `all`
// holds every value of the dump by key (the filter's time needs the period).
export function settingMeaning(key, value, all = {}) {
  const k = String(key || "").toUpperCase();
  const describe =
    SETTING_MEANINGS[k] || (/^MBCFG/.test(k) ? () => "Modbus settings." : null);
  return describe ? describe(String(value ?? ""), all) : "";
}

// The pieces of a download's file name: the node as plain characters, and a
// time as compact UTC ("20261009T031210Z").
export const nodeSlug = (mac) =>
  String(mac || "")
    .replace(/[^0-9a-z]/gi, "")
    .toLowerCase() || "node";
export function utcStamp(value) {
  const ms = timeOf(value);
  return ms === null
    ? "unknown-time"
    : new Date(ms)
        .toISOString()
        .replace(/\.\d+Z$/, "Z")
        .replace(/[-:]/g, "");
}

export function captureFileName(mac, capture) {
  return `radar-capture-${nodeSlug(mac)}-${capture?.id ?? "waterfall"}-${utcStamp(capture?.t)}.json`;
}

// Everything known about one capture, for a file: the stored capture as
// radar-captures/<id>/ serves it, the stream record and our analysis.
export function captureExport({
  mac,
  capture,
  detail,
  detailError,
  analysis,
  exportedAt,
}) {
  return {
    exported_at: exportedAt,
    mac,
    notes: [
      "Spectrum dB = 20·log10(bin value), assuming linear magnitude (unconfirmed).",
      "Distances = bin × step_mm − cal_mm, as the node reports them.",
      "analysis is the dashboard's own quick look, not the radar's.",
    ],
    capture: detail || null,
    ...(detailError ? { capture_error: detailError } : {}),
    stream: capture,
    analysis: analysis || null,
  };
}
